import { ProCard, StatusBadge } from '@components'
import KpiCard from '@dashboard/components/KpiCard'
import cn from '@integrations/classnames'
import { selectCurrentUserId, useAppSelector, useGetUserExpenseLimitsQuery } from 'mmfintech-backend-api'
import { formatMoney, tr } from 'mmfintech-commons'
import { ProgressBar } from 'mmfintech-portal-commons'
import styles from './SpendingLimitBarUser.module.scss'

// From this share of the limit on, the card warns that the limit is close.
const NEAR_LIMIT_PERCENTAGE = 85

// The signed-in staff member's own monthly limit: spent, limit and what is left this month.
function SpendingLimitBarUser() {
  const merchantUserId = useAppSelector(selectCurrentUserId)
  const { data: userExpenseLimits } = useGetUserExpenseLimitsQuery({ merchantUserId })

  const limit = Number(userExpenseLimits?.monthlyVolume) || 0

  // No limit set: nothing to track, so the card stays out of the way.
  if (!limit) {
    return null
  }

  const { currency } = userExpenseLimits
  const spent = Number(userExpenseLimits.spentMonthlyVolume) || 0
  const available = Math.max(Number(userExpenseLimits.availableMonthlyVolume) || 0, 0)
  const usedPercentage = Math.min(Math.round((spent / limit) * 100), 100)
  const reached = spent >= limit
  const near = !reached && usedPercentage >= NEAR_LIMIT_PERCENTAGE

  return (
    <ProCard tone='action' className={cn('spending-tracker')} data-test='spending-tracker'>
      <div className={cn(styles['spending-tracker__layout'], 'spending-tracker__layout')}>
        <div className={cn(styles['spending-tracker__spent'], 'spending-tracker__spent')}>
          <KpiCard.Header label={tr('FRONTEND.DASHBOARD.SPENDING_LIMIT.MONTHLY', 'Monthly spending limit')} />
          <KpiCard.Value
            unit={tr('FRONTEND.DASHBOARD.SPENDING_LIMIT.SPENT_OF', 'spent of {{LIMIT}}', {
              LIMIT: formatMoney(limit, currency)
            })}
          >
            {formatMoney(spent, currency)}
          </KpiCard.Value>
        </div>

        <div className={cn(styles['spending-tracker__available'], 'spending-tracker__available')}>
          <KpiCard.Sub>
            <strong data-test='spending-tracker-left'>
              {tr('FRONTEND.DASHBOARD.SPENDING_LIMIT.LEFT', '{{AMOUNT}} left', {
                AMOUNT: formatMoney(available, currency)
              })}
            </strong>
          </KpiCard.Sub>
          <KpiCard.Sub>
            {reached
              ? tr('FRONTEND.DASHBOARD.SPENDING_LIMIT.NOTHING_LEFT', 'nothing left this month')
              : tr('FRONTEND.DASHBOARD.SPENDING_LIMIT.TO_SPEND', 'to spend this month')}
          </KpiCard.Sub>
        </div>

        <div className={cn(styles['spending-tracker__meter'], 'spending-tracker__meter')}>
          {/* threshold 0: the bar turns red only once the limit is reached */}
          <ProgressBar value={spent} max={limit} threshold={0} className={cn('spending-tracker-progress')} />

          <div className={cn(styles['spending-tracker__note'], 'spending-tracker__note')}>
            <KpiCard.Sub>
              <strong data-test='spending-tracker-used'>{usedPercentage}%</strong>{' '}
              {tr('FRONTEND.DASHBOARD.SPENDING_LIMIT.USED_SET_BY_ADMIN', 'used this month · Set by an administrator')}
            </KpiCard.Sub>
            {reached ? (
              <StatusBadge tone='error' dot={false} data-test='spending-tracker-warning'>
                {tr('FRONTEND.DASHBOARD.SPENDING_LIMIT.REACHED', 'Limit reached. Ask an administrator to raise it.')}
              </StatusBadge>
            ) : (
              near && (
                // The warning tint disappears on the yellow card; the neutral chip stays readable
                <StatusBadge tone='neutral' dot={false} data-test='spending-tracker-warning'>
                  {tr('FRONTEND.DASHBOARD.SPENDING_LIMIT.NEAR', 'Almost at your limit.')}
                </StatusBadge>
              )
            )}
          </div>
        </div>
      </div>
    </ProCard>
  )
}

export default SpendingLimitBarUser
